import { useCallback, useContext, useState } from "react";
import StageProductContext from "./forms/StageProductContext";
import { CircularProgress, Icon, IconButton } from "@mui/material";
import useDeleteProposalFile from "hooks/proposals/useDeleteProposalFile";
import { Reference, useApolloClient } from "@apollo/client";
import {
  DeleteProposalFileMutationVariables,
  DeleteProposalMapImageMutationVariables,
  DeleteProposalProductProjectFileMutationVariables,
} from "generated/graphql";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useDeleteProposalMapImage from "hooks/proposals/useProposalMapImage";
import useDeleteProposalProductProjectFile from "hooks/proposals/useDeleteProposalProductProjectFile";
import {
  AttachmentKind,
  getAttachmentKind,
  ProjectFileGalleryCard,
} from "modules/ProjectFiles/ProjectFileGalleryCard";

enum FileOrigin {
  ProjectFile,
  ProductFile,
  Map,
}

interface File {
  id?: string;
  origin: FileOrigin;
  href: string;
  name?: string;
  projectFileId?: string;
  kind: AttachmentKind;
}

export default function ProposalProductFiles() {
  const client = useApolloClient();
  const { product } = useContext(StageProductContext);
  const [deleteProposalFile, { getData }] = useDeleteProposalFile();
  const [deleteProposalProductProjectFile, { getData: getProjectFileData }] =
    useDeleteProposalProductProjectFile();
  const [deleteProposalMapImage, { getData: mapImageGetData }] = useDeleteProposalMapImage();
  const [index, setIndex] = useState(-1);

  const attachments = product.files.reduce((acc, productFile) => {
    acc.push({
      id: productFile.id,
      origin: FileOrigin.ProductFile,
      href: `${import.meta.env.VITE_CDN_BASE_URL}/${productFile.filename}`,
      name: productFile.filename,
      kind: getAttachmentKind(productFile.filename),
    });

    return acc;
  }, [] as Array<File>);

  if (product?.mapImage && !product?.mapImageDirty) {
    attachments.push({
      href: `${import.meta.env.VITE_CDN_BASE_URL}/${product.mapImage}`,
      origin: FileOrigin.Map,
      name: "Map image",
      kind: AttachmentKind.Image,
    });
  }

  if (product?.projectFiles && product?.projectFiles.length > 0) {
    product.projectFiles.forEach((projectFile) => {
      attachments.push({
        href: `${import.meta.env.VITE_CDN_BASE_URL}/${projectFile.path}`,
        origin: FileOrigin.ProjectFile,
        id: projectFile.pivot?.id,
        projectFileId: projectFile.id,
        name: projectFile.name,
        kind: getAttachmentKind(projectFile.name),
      });
    });
  }

  const imageAttachments = attachments.filter(({ kind }) => kind === AttachmentKind.Image);
  const slides = imageAttachments.map(({ href }) => {
    return { src: href };
  });

  const removeFileFromCache = useCallback(
    (success, id, field: "files" | "projectFiles") => {
      if (success) {
        const idToRemove = id;
        client.cache.modify({
          id: client.cache.identify(product),
          fields: {
            [field]: (existingFilesRef: Reference[], { readField }) => {
              return existingFilesRef.filter((fileRef) => {
                return idToRemove !== readField("id", fileRef);
              });
            },
          },
        });
      }
    },
    [client]
  );

  const onDeleteMapImage = useCallback(
    async (variables: DeleteProposalMapImageMutationVariables) => {
      const result = await deleteProposalMapImage({
        variables,
      });
      const success = mapImageGetData(result);
      if (success) {
        client.cache.modify({
          id: client.cache.identify(product),
          fields: {
            mapImage: () => null,
            mapImageDirty: () => false,
          },
        });
      }
    },
    [client.cache, deleteProposalMapImage, product]
  );

  const onDelete = useCallback(
    async (variables: DeleteProposalFileMutationVariables) => {
      const result = await deleteProposalFile({
        variables,
      });
      const success = getData(result);
      removeFileFromCache(success, variables.id, "files");
    },
    [deleteProposalFile, removeFileFromCache]
  );

  const onDeleteProjectFile = useCallback(
    async (variables: DeleteProposalProductProjectFileMutationVariables, projectFileId) => {
      const result = await deleteProposalProductProjectFile({
        variables,
      });
      const success = getProjectFileData(result);
      removeFileFromCache(success, projectFileId, "projectFiles");
    },
    [deleteProposalProductProjectFile, removeFileFromCache]
  );

  const handleOnDelete = ({ origin, id, projectFileId }: File) => {
    switch (origin) {
      case FileOrigin.ProductFile:
        return onDelete({
          id,
        });

      case FileOrigin.ProjectFile:
        return onDeleteProjectFile(
          {
            id,
          },
          projectFileId
        );

      case FileOrigin.Map:
        return onDeleteMapImage({
          id: product.id,
        });
    }
  };

  return (
    <div>
      <Lightbox open={index >= 0} index={index} close={() => setIndex(-1)} slides={slides} />
      <MDBox display="flex" flexWrap="wrap" gap={2}>
        {attachments.map((file, i) => {
          const imageIndex =
            file.kind === AttachmentKind.Image
              ? imageAttachments.findIndex(({ href }) => href === file.href)
              : undefined;

          return (
            <ProjectFileGalleryCard
              key={`${file.origin}-${file.id || file.href}-${i}`}
              file={file}
              kind={file.kind}
              onPrimaryClick={() => {
                if (file.kind === AttachmentKind.Image && typeof imageIndex === "number") {
                  setIndex(imageIndex);
                  return;
                }

                window.open(file.href, "_blank", "noopener,noreferrer");
              }}
              onDelete={() => {
                handleOnDelete(file);
              }}
            />
          );
        })}
      </MDBox>

      {product?.mapImageDirty && !product?.mapImage && (
        <MDBox display="flex" gap={5} alignItems="center">
          <MDTypography variant="h6">
            Generating Map Image... (Takes up to 5 mins, but you may continue working)
          </MDTypography>
          <CircularProgress color="info" />
        </MDBox>
      )}
    </div>
  );
}
